import React, { useEffect, useCallback, useState } from 'react';
import { Tag, Search, Trash2 } from 'lucide-react';
import localApiAdapter from '../../core/services/localApiAdapter';
import {
  fetchHrEmployees,
  normalizeHrEmployeesResponse,
} from '../ressources-humaines/hrListPageUtils';
import {
  HR_EMPLOYEES_REFRESH_EVENT,
  useHrApiRefreshListener,
} from '../ressources-humaines/hrRefreshEvents';
import {
  sliceQawaemStats,
  QAWAEM_KPI_GRID_CLASS,
} from '../../products/saudi/components/QawaemErpListPageShell';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';
import {
  QawaemTableStickyActionsTd,
  QawaemTableStickyActionsFooterTd,
  mergeStickyActionsClass,
  mergeStickySelectClass,
} from '../../products/saudi/components/QawaemTableStickyActions';

export { sliceQawaemStats, QAWAEM_KPI_GRID_CLASS };
export {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
  QawaemTableStickyActionsTd,
  QawaemTableStickyActionsFooterTd,
  mergeStickyActionsClass,
  mergeStickySelectClass,
};

/** Actions column header class — pinned during horizontal scroll. */
export const QAWAEM_TABLE_ACTIONS_TH_CLASS = mergeStickyActionsClass(
  'px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle',
);

/** Standard 3-column form grid — matches Products.js / QawaemErpCrudModal layout. */
export const SALAIRES_FORM_GRID_CLASS = 'grid grid-cols-1 md:grid-cols-3 gap-4';

/** @deprecated Use SALAIRES_FORM_GRID_CLASS — kept as alias for legacy imports */
export const SALAIRES_FORM_GRID_2COL_CLASS = SALAIRES_FORM_GRID_CLASS;

/** Clear card-view search when switching back to table (column header filters in table). */
export function useClearSearchOnTableView(viewMode, searchTerm, onSearchChange) {
  useEffect(() => {
    if (viewMode === 'table' && onSearchChange && String(searchTerm || '').trim()) {
      onSearchChange('');
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps
}

/** Row selection helpers — delegates to Qawaem filtered selection (Variantes pattern). */
export function useSalairesTableSelection(filteredItems = []) {
  return useQawaemFilteredRowSelection(filteredItems || []);
}

/** Bulk delete button for Qawaem title-row filterActions slot. */
export function renderSalairesBulkDeleteFilterAction({ selectedCount, onBulkDelete, t, label }) {
  if (!selectedCount) return null;
  return (
    <button
      type="button"
      onClick={onBulkDelete}
      className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
    >
      <Trash2 size={16} />
      <span>{label || t('common.delete')} ({selectedCount})</span>
    </button>
  );
}

/** Legacy-brand controls strip — search in cards view only + table/cards toggle. */
export function SalairesListControlsStrip({
  hidden = false,
  viewMode = 'table',
  onViewModeChange,
  searchTerm = '',
  onSearchChange,
  searchPlaceholder = 'بحث...',
  isDashboardLightMode = false,
  cardsTitle,
  tableTitle,
  filterActions = null,
}) {
  if (hidden) return null;

  return (
    <div className="facturation-list-controls-strip p-4 border-b border-white/10">
      <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
        {filterActions}
        {viewMode === 'cards' && onSearchChange ? (
          <div className="relative min-w-0 flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        ) : null}
        {onViewModeChange ? (
          <FacturationViewModeToggle
            viewMode={viewMode}
            onViewModeChange={onViewModeChange}
            isLight={isDashboardLightMode}
            cardsTitle={cardsTitle}
            tableTitle={tableTitle}
          />
        ) : null}
      </div>
    </div>
  );
}

/** Map salaires stat config to FacturationListPageLayout stats (max 4). */
export function mapSalairesStatCards(entries = [], max = 4) {
  return sliceQawaemStats(entries, max).map((stat, i) => ({
    key: stat.key || stat.label || stat.title || i,
    label: stat.label || stat.title,
    value: stat.value,
    icon: stat.icon,
    variant: stat.variant || (i % 2 === 0 ? 'copper' : 'forest'),
    color: stat.color,
    className: stat.className,
  }));
}

/** Build column picker defs from { key, labelKey? } + label fn `p`. */
export function buildSalairesColumnDefs(columns, p) {
  return columns.map((col) => ({
    key: col.key,
    label: col.labelKey ? p(col.labelKey) : col.label,
    checked: col.checked !== false,
    required: col.required,
  }));
}

/** Period dropdown labels from page `p()` helper. */
export function buildSalairesPeriodLabels(p, t) {
  return {
    label: p('period', { defaultValue: t('common.period', { defaultValue: 'الفترة' }) }),
    startLabel: p('start_date', { defaultValue: t('common.start_date', { defaultValue: 'تاريخ البداية' }) }),
    endLabel: p('end_date', { defaultValue: t('common.end_date', { defaultValue: 'تاريخ النهاية' }) }),
    resetLabel: p('reset', { defaultValue: t('common.reset', { defaultValue: 'إعادة تعيين' }) }),
    applyLabel: p('apply', { defaultValue: t('common.apply', { defaultValue: 'تطبيق' }) }),
    selectPlaceholder: p('select', { defaultValue: t('common.select', { defaultValue: 'اختر' }) }),
  };
}

/** Secondary status/category filter for title row. */
export function buildSalairesSecondaryFilter({
  value,
  onChange,
  options,
  filterAllValue = 'all',
  filterAllLabel,
  icon = Tag,
}) {
  const normalized = !value || value === filterAllValue ? filterAllValue : value;
  const current = options.find((o) => o.value === normalized);
  return {
    label: current?.label || filterAllLabel || options[0]?.label,
    value: normalized,
    options,
    onChange: (val) => onChange(val === filterAllValue ? filterAllValue : val),
    icon,
  };
}

const salairesImportExportNoop = () => {};

/** Import/export toolbar props — pass `handlers` when the page supports I/O. */
export function buildSalairesImportExportProps({
  showImportDropdown,
  setShowImportDropdown,
  showExportDropdown,
  setShowExportDropdown,
  importDropdownRef,
  exportDropdownRef,
  p,
  t,
  importLabel,
  exportLabel,
  generatingLabel,
  isGeneratingPDF = false,
  handlers = {},
}) {
  return {
    importOpen: showImportDropdown,
    exportOpen: showExportDropdown,
    onToggleImport: () => {
      setShowImportDropdown(!showImportDropdown);
      setShowExportDropdown(false);
    },
    onToggleExport: () => {
      setShowExportDropdown(!showExportDropdown);
      setShowImportDropdown(false);
    },
    importRef: importDropdownRef,
    exportRef: exportDropdownRef,
    importLabel: importLabel || p?.('import', { defaultValue: t?.('common.import', { defaultValue: 'استيراد' }) }),
    exportLabel: exportLabel || t?.('common.export', { defaultValue: 'تصدير' }),
    generatingLabel: generatingLabel || p?.('generating', { defaultValue: t?.('common.loading', { defaultValue: 'جاري الإنشاء...' }) }),
    isGeneratingPDF,
    onImportCSV: handlers.onImportCSV || handlers.onImport || salairesImportExportNoop,
    onImportExcel: handlers.onImportExcel || handlers.onImport || salairesImportExportNoop,
    onImportJSON: handlers.onImportJSON || salairesImportExportNoop,
    onExportPDF: handlers.onExportPDF || salairesImportExportNoop,
    onExportExcel: handlers.onExportExcel || salairesImportExportNoop,
    onExportCSV: handlers.onExportCSV || handlers.onExport || salairesImportExportNoop,
    onExportJSON: handlers.onExportJSON || salairesImportExportNoop,
    showJsonImportExport: handlers.showJsonImportExport ?? false,
  };
}

/** Fetch active employees from HR for salaires entity pickers. */
export async function fetchSalairesEmployees({ status = 'active' } = {}) {
  try {
    if (status) {
      const response = await localApiAdapter.get('/hr/employees', { status });
      return normalizeHrEmployeesResponse(response);
    }
    return fetchHrEmployees();
  } catch (error) {
    console.error('Error fetching salaires employees:', error);
    return [];
  }
}

/** Live HR employee list — refreshes when employees change in HR module. */
export function useSalairesEmployeeOptions({ status = 'active' } = {}) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setEmployees(await fetchSalairesEmployees({ status }));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useHrApiRefreshListener(HR_EMPLOYEES_REFRESH_EVENT, refresh);

  return { employees, loading, refresh };
}
